"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { showToast } from "../../../components/custom/custom-toast";
import { isSuccess } from "../../../models/response/base-response";
import UnitModal, { UnitFormValues } from "../components/unit-modal";
import UnitsTable from "../components/units-table";
import UnitService, {
  MEASURE_TYPE_NAME,
  UNIT_STATUS,
  UNIT_STATUS_NAME,
  Unit,
} from "../services/unit-service";

function extractList(raw: any): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    const arr = raw.list ?? raw.data;
    if (Array.isArray(arr)) return arr;
  }
  return [];
}

const UnitsIndexPage: React.FC = () => {
  const [rows, setRows] = useState<Unit[]>([]);
  const [filterMeasure, setFilterMeasure] = useState<number | undefined>();
  const [filterStatus, setFilterStatus] = useState<number | undefined>();

  const [openModal, setOpenModal] = useState(false);
  const [editRow, setEditRow] = useState<Unit | null>(null);

  const { fetch: listUnits, loading: loadingList } = UnitService.list();
  const { fetch: createUnit, loading: creating } = UnitService.create();
  const { fetch: updateUnit, loading: updating } = UnitService.update();
  const { fetch: changeStatus } = UnitService.changeStatus();

  const listRef = useRef(listUnits);
  useEffect(() => {
    listRef.current = listUnits;
  }, [listUnits]);

  const load = useCallback(async () => {
    const res = await listRef.current({
      measure_type: filterMeasure,
      status: filterStatus ?? -1,
      limit: 200,
    });
    if (res && isSuccess(res)) setRows(extractList(res.data));
  }, [filterMeasure, filterStatus]);

  useEffect(() => {
    void load();
  }, [load]);

  const onCreate = async (values: UnitFormValues): Promise<boolean> => {
    try {
      const res = await createUnit(values);
      if (res && isSuccess(res)) {
        showToast({ message: "Tạo đơn vị thành công", type: "success" });
        void load();
        return true;
      }
      // Backend chặn: trùng mã, hoặc đơn vị gốc khác nhóm đo lường.
      showToast({ message: res?.message || "Tạo đơn vị thất bại", type: "error" });
      return false;
    } catch (err) {
      console.error("[onCreate] Error:", err);
      showToast({ message: "Lỗi khi tạo đơn vị", type: "error" });
      return false;
    }
  };

  const onUpdate = async (values: UnitFormValues): Promise<boolean> => {
    if (!editRow) return false;
    try {
      const res = await updateUnit(editRow.id, values);
      if (res && isSuccess(res)) {
        showToast({ message: "Cập nhật đơn vị thành công", type: "success" });
        void load();
        return true;
      }
      showToast({ message: res?.message || "Cập nhật thất bại", type: "error" });
      return false;
    } catch (err) {
      console.error("[onUpdate] Error:", err);
      showToast({ message: "Lỗi khi cập nhật đơn vị", type: "error" });
      return false;
    }
  };

  const onToggleStatus = async (row: Unit) => {
    const next = row.status === UNIT_STATUS.ACTIVE ? UNIT_STATUS.INACTIVE : UNIT_STATUS.ACTIVE;
    try {
      const res = await changeStatus(row.id, next);
      if (res && isSuccess(res)) {
        showToast({
          message: next === UNIT_STATUS.ACTIVE ? "Đã dùng lại đơn vị" : "Đã ngưng sử dụng",
          type: "success",
        });
        void load();
        return;
      }
      showToast({ message: res?.message || "Đổi trạng thái thất bại", type: "error" });
    } catch (err) {
      console.error("[onToggleStatus] Error:", err);
      showToast({ message: "Lỗi khi đổi trạng thái", type: "error" });
    }
  };

  return (
    <div className="space-y-6">
      {/* ── HEADER ── */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-green-600 tracking-tight">Đơn vị</h1>
          {/* <p className="text-sm text-gray-500 mt-1">
            Đơn vị bán của vật tư và đơn vị đo lường cho định lượng. Nhóm đo lường quyết định vật tư
            có vào biên bản kiểm đếm hay không.
          </p> */}
        </div>

        <button
          type="button"
          onClick={() => {
            setEditRow(null);
            setOpenModal(true);
          }}
          className="group inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-bold text-white shadow-lg transition-all bg-green-600 shadow-green-200 hover:bg-green-700 hover:-translate-y-0.5 cursor-pointer"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 transition-transform group-hover:rotate-90">
            <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
          </svg>
          <span>THÊM ĐƠN VỊ</span>
        </button>
      </div>

      {/* ── MAIN CONTENT CARD ── */}
      <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex flex-wrap items-center gap-3">
          <select
            value={filterMeasure ?? ""}
            onChange={(e) => setFilterMeasure(e.target.value ? Number(e.target.value) : undefined)}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-500/10 transition-all cursor-pointer"
          >
            <option value="">Tất cả nhóm đo lường</option>
            {Object.entries(MEASURE_TYPE_NAME).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>

          <select
            value={filterStatus ?? ""}
            onChange={(e) => setFilterStatus(e.target.value ? Number(e.target.value) : undefined)}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-500/10 transition-all cursor-pointer"
          >
            <option value="">Tất cả trạng thái</option>
            {Object.entries(UNIT_STATUS_NAME).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>

        <div className="p-0">
          <UnitsTable
            rows={rows}
            isLoading={loadingList}
            onEdit={(row) => {
              setEditRow(row);
              setOpenModal(true);
            }}
            onToggleStatus={onToggleStatus}
          />
        </div>

        <div className="border-t border-gray-100 bg-gray-50/50 px-6 py-4">
          <div className="text-sm text-gray-500 font-medium">
            {rows.length > 0 ? `Tổng cộng ${rows.length} đơn vị` : "Chưa có đơn vị nào"}
          </div>
        </div>
      </div>

      <UnitModal
        isOpen={openModal}
        onClose={() => {
          setOpenModal(false);
          setEditRow(null);
        }}
        onSubmit={editRow ? onUpdate : onCreate}
        loading={creating || updating}
        editData={editRow}
        units={rows}
      />
    </div>
  );
};

export default UnitsIndexPage;
